import { useCallback, useEffect, useState } from "react";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import { clock } from "../system/format";

// ---------------------------------------------------------------------------
// SWEEP S12. THE ROLL CALL THE WORKER APP SENDS PEOPLE TO.
//
// Under `supervisor_confirmed` — the method for a venue with no signal and no hardware,
// and D11's fallback for one with no coordinates — the worker app says "ask your
// supervisor to confirm you", and nothing let anybody confirm them. This is that screen
// for the console's Live ops: one row per person on a post, what has been recorded, and
// the next thing that can be.
//
// IT DECIDES NOTHING. The next kind comes from the server (`next_kind`), the clock is
// the authoritative source from the reference list, the method in force and whether a
// confirmer is owed are `app.record_attendance`'s (0161), and every refusal — a briefing
// not acknowledged, a window not open yet, a geofence method that needs the worker's own
// phone — arrives as the server's sentence, word for word.
// ---------------------------------------------------------------------------

type CrewRow = {
  assignment_id: string;
  given_name: string;
  family_name: string;
  status: string;
  is_supervisor: boolean;
  kind: string | null;
  effective_at: string | null;
  minutes_late: number | null;
  next_kind: string | null;
  next_kind_label: string | null;
};

type Person = {
  assignment_id: string;
  name: string;
  status: string;
  is_supervisor: boolean;
  recorded: { kind: string; at: string; minutes_late: number | null }[];
  next_kind: string | null;
  next_kind_label: string | null;
};

/** The reply has one row per recorded event; the roll call wants one per person. */
export function people(rows: CrewRow[]): Person[] {
  const byId = new Map<string, Person>();
  for (const r of rows) {
    const p = byId.get(r.assignment_id) ?? {
      assignment_id: r.assignment_id,
      name: `${r.given_name} ${r.family_name}`,
      status: r.status,
      is_supervisor: r.is_supervisor,
      recorded: [],
      next_kind: r.next_kind,
      next_kind_label: r.next_kind_label,
    };
    if (r.kind && r.effective_at) p.recorded.push({ kind: r.kind, at: r.effective_at, minutes_late: r.minutes_late });
    byId.set(r.assignment_id, p);
  }
  return [...byId.values()];
}

export function RollCall({ requirementId, onRecorded }: { requirementId: string; onRecorded: () => Promise<void> }) {
  const { apiBase } = useSession();
  const { locale, t, refusal } = useT();
  const [crew, setCrew] = useState<Person[] | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [kindLabel, setKindLabel] = useState<Map<string, string>>(new Map());
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ who: string; text: string } | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, s, k] = await Promise.all([
        send<{ crew?: CrewRow[]; error?: { message?: string } }>(console_.requirementAttendance(apiBase, requirementId)),
        send<{ clock_sources?: { id: string; is_authoritative: boolean }[] }>(console_.clockSources(apiBase)),
        send<{ attendance_kinds?: { id: string; label: string }[] }>(console_.attendanceKinds(apiBase)),
      ]);
      setKindLabel(new Map((k.body.attendance_kinds ?? []).map((x) => [x.id, x.label])));
      if (c.status !== 200 || !c.body.crew) {
        setFailed(refusal(c.body.error) ?? t("This crew could not be read."));
        return;
      }
      setCrew(people(c.body.crew));
      setSource(s.body.clock_sources?.find((x) => x.is_authoritative)?.id ?? null);
    } catch {
      setFailed(t("We could not reach Verbunk. Check your connection and try again."));
    }
  }, [apiBase, requirementId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function record(p: Person) {
    if (!p.next_kind || !source) return;
    setBusy(p.assignment_id);
    setNotice(null);
    try {
      const res = await send<{ attendance?: { replayed?: boolean }; error?: { message?: string } }>(
        console_.recordAttendance(apiBase, p.assignment_id, {
          kind: p.next_kind,
          clock_source: source,
          idempotency_key: crypto.randomUUID(),
        }),
      );
      if (res.status !== 200) {
        setNotice({ who: p.assignment_id, text: refusal(res.body.error) ?? t("That could not be recorded.") });
        return;
      }
      await load();
      await onRecorded();
    } catch {
      setNotice({ who: p.assignment_id, text: t("We could not reach Verbunk. Check your connection and try again.") });
    } finally {
      setBusy(null);
    }
  }

  if (failed) return <StatusChip tone="failed" label={failed} />;
  if (crew === null) return <p className="muted">{t("Reading the crew…")}</p>;
  if (crew.length === 0) return <p className="muted">{t("Nobody is booked on this post.")}</p>;

  return (
    <>
    {/* Every Record button waits for the authoritative clock; say why when it is missing. */}
    {!source ? <p className="muted">{t("The clock could not be read, so nothing can be recorded. Reload to try again.")}</p> : null}
    <ul className="plain-list">
      {crew.map((p) => (
        <li key={p.assignment_id} className="stack">
          <div className="row">
            <strong>{p.name}</strong>
            {p.is_supervisor ? <span className="muted">{t("Supervisor")}</span> : null}
            {p.recorded.map((r) => (
              <span key={r.kind} className="muted">
                {translateLabel(locale, kindLabel.get(r.kind) ?? r.kind)} {clock(r.at)}
                {r.minutes_late !== null && r.minutes_late > 0 ? ` ${t("({n} min late)", { n: r.minutes_late })}` : ""}
              </span>
            ))}
            {p.next_kind ? (
              <button
                type="button"
                className="btn btn-quiet"
                disabled={busy !== null || !source}
                onClick={() => void record(p)}
              >
                {busy === p.assignment_id
                  ? t("Recording…")
                  : t("Record: {kind}", { kind: translateLabel(locale, p.next_kind_label ?? p.next_kind) })}
              </button>
            ) : (
              <StatusChip tone="live" label={t("Done")} />
            )}
          </div>
          {notice?.who === p.assignment_id ? (
            <div className="form-message" role="status" aria-live="polite">
              <StatusChip tone="attention" label={t("Not recorded")} />
              <span>{notice.text}</span>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
    </>
  );
}
